import React from 'react';
import { FlaskConical, AlertTriangle } from 'lucide-react';
import { useTestMode } from '../../contexts/TestModeContext';

/**
 * Global banner shown at the top of the screen when test mode is active
 */
export const TestModeBanner: React.FC = () => {
  const { isTestMode, toggleTestMode } = useTestMode();

  if (!isTestMode) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-[100] bg-warning text-black px-4 py-2">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FlaskConical size={18} className="animate-pulse" />
          <span className="font-bold text-sm">TEST MODE ACTIVE</span>
          <span className="text-sm opacity-80 hidden sm:inline">
            - All data is isolated in test-mode/ path. Stripe uses test keys.
          </span>
        </div>
        <button
          onClick={toggleTestMode}
          className="px-3 py-1 bg-black/20 hover:bg-black/30 rounded text-sm font-medium transition-colors"
        >
          Exit Test Mode
        </button>
      </div>
    </div>
  );
};

/**
 * Warning box shown in modals/forms when test mode is active
 */
export const TestModeWarning: React.FC<{ action?: string }> = ({ action = 'This action' }) => {
  const { isTestMode } = useTestMode();

  if (!isTestMode) return null;

  return (
    <div className="flex items-start gap-3 p-4 bg-warning/10 border border-warning/30 rounded-xl mb-4">
      <AlertTriangle size={20} className="text-warning flex-shrink-0 mt-0.5" />
      <div>
        <p className="font-semibold text-warning text-sm">Test Mode Active</p>
        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
          {action} will use <strong>test Stripe keys</strong> and data will be saved to the <strong>test-mode/</strong> database path.
        </p>
      </div>
    </div>
  );
};

/**
 * Small indicator badge for test mode
 */
export const TestModeBadge: React.FC = () => {
  const { isTestMode } = useTestMode();

  if (!isTestMode) return null;

  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-warning text-black text-xs font-bold rounded-full">
      <FlaskConical size={12} />
      TEST
    </span>
  );
};

export default TestModeBanner;
